/**
 * Advanced → Spam protection — the whole API of the screen that configures an
 * application's bot check.
 *
 * Administration on the PLATFORM host: a project domain serves the running
 * application, and the application's own bot-check credentials are not part of
 * running it (the backend refuses this prefix there — see
 * `middleware/domain_scope`). The publishable half — the site key a page needs
 * in order to execute — is served from `/website/...` instead and fetched by
 * `api/siteCaptcha.ts`.
 */

import api from './client'

export type CaptchaSurface = 'forms' | 'auth' | 'transactions' | 'uploads'
export type LowScorePolicy = 'flag' | 'reject'

export interface InheritedCaptcha {
  source: 'platform'
  site_key: string
  min_score: number
  /** False when the instance has a site key but no secret — a check that
   *  would run in every browser and never be verified. */
  usable: boolean
}

export interface ProjectCaptcha {
  /** False = this project has no row; it inherits, or runs no check at all. */
  configured: boolean
  id?: number
  provider: string
  enabled: boolean
  site_key: string
  /** `••••…3f9a`. The screen never receives a usable secret. */
  secret_masked: string
  has_secret: boolean
  min_score: number
  on_low_score: LowScorePolicy
  protect: Record<CaptchaSurface, boolean>
  verified_at: string | null
  last_error: string
  updated_at?: string
  /** What is in force when this project has configured nothing of its own. */
  inherited: InheritedCaptcha | null
}

export interface CaptchaConfig {
  providers: string[]
  surfaces: CaptchaSurface[]
  low_score_policies: LowScorePolicy[]
  default_min_score: number
}

export interface CaptchaSave {
  enabled?: boolean
  site_key?: string
  /** Omitted or "" leaves the stored secret alone; "-" clears it. */
  secret_key?: string
  min_score?: number
  on_low_score?: LowScorePolicy
  protect_forms?: boolean
  protect_auth?: boolean
  protect_transactions?: boolean
  protect_uploads?: boolean
}

export async function getCaptchaConfig(): Promise<CaptchaConfig> {
  const { data } = await api.get<CaptchaConfig>('/captcha/config')
  return data
}

export async function getProjectCaptcha(project: string): Promise<ProjectCaptcha> {
  const { data } = await api.get<{ captcha: ProjectCaptcha }>(
    `/captcha/project/${encodeURIComponent(project)}`)
  return data.captcha
}

export async function saveProjectCaptcha(
  project: string, body: CaptchaSave,
): Promise<ProjectCaptcha> {
  const { data } = await api.put<{ captcha: ProjectCaptcha }>(
    `/captcha/project/${encodeURIComponent(project)}`, body)
  return data.captcha
}

export async function testProjectCaptcha(
  project: string,
): Promise<{ ok: boolean; error?: string; verified_at?: string }> {
  const { data } = await api.post(
    `/captcha/project/${encodeURIComponent(project)}/test`)
  return data
}

export async function deleteProjectCaptcha(project: string): Promise<ProjectCaptcha> {
  const { data } = await api.delete<{ captcha: ProjectCaptcha }>(
    `/captcha/project/${encodeURIComponent(project)}`)
  return data.captcha
}
